import React, {
  useEffect, useMemo, useState,
} from "react";
import { toast } from "react-toastify";
import {
  Button,
  Segmented,
  Select,
  Spin,
  Tooltip,
  Upload,
} from "antd";
import { EditOutlined, InboxOutlined, DeleteOutlined } from "@ant-design/icons";
import { getTestRules } from "../../../actions/chemistry";
import { createChemUploadMethodMap } from "./ChemUploadRegistry";
import {
  isLegacyChemUploadFileNamePairAllowed,
  resolveChemUploadMethodKeyFromFileAndTest,
} from "./ChemUploadResolve";
import { hasPermission } from "../../../utils/helpers";
import ChemHistory from "./Components/History/ChemHistory";
import ChemAddNewTest from "./ChemAddNewTest";
import ChemUploadResultsModal from "./ChemUploadResultsModal";
import "./ChemUpload.css";

// Builds detail rows for the selected test (result/parse type, columns for standard parsing, units).
const buildSelectedTestDetails = (selectedTestRule, isMlParsingEnabled) => {
  if (!selectedTestRule) {
    return [];
  }

  const detailItems = [];
  const hasResultType = selectedTestRule.result_type !== null && selectedTestRule.result_type !== "";
  const addDetail = (label, value) => {
    if (value !== null && value !== "") {
      detailItems.push({ label, value: String(value) });
    }
  };

  if (hasResultType) {
    addDetail("Parse Type", isMlParsingEnabled ? "Smart(AI)" : "Standard");
    addDetail(
      "Result Type",
      selectedTestRule.result_type === "float" ? "Decimal" : "Integer",
    );
  }

  addDetail("Unit", selectedTestRule.units);

  if (!isMlParsingEnabled) {
    addDetail("Genista ID Column", selectedTestRule.genistaid_cell);
    addDetail("Result Column", selectedTestRule.result_cell);
  }

  return detailItems;
};

// Checks if a file's name ends with any of the allowed extensions (case-insensitive)
const fileMatchesUploadFileTypes = (file, fileTypeList) => {
  const name = String(file?.name).toLowerCase();
  return (fileTypeList || []).some((ext) => name.endsWith(String(ext).toLowerCase()));
};

// Returns user-facing support text based on active upload method and parse mode.
const getUploadSupportText = (activeUploadMethod, isMlParsingEnabled) => {
  if (!activeUploadMethod?.acceptedFileTypes?.length) {
    return "This test is not supported for upload.";
  }
  return isMlParsingEnabled
    ? "Excel and image files are supported"
    : "Only Excel files are supported";
};

// Checks if the upload method is supported.
const isUploadMethodSupported = (activeUploadMethod) => (
  Boolean(activeUploadMethod?.acceptedFileTypes?.length)
  && typeof activeUploadMethod?.uploadMethod === "function"
);

// Text under the drag area: ML tests may attach several files; other tests are limited to one.
const getUploadSelectionHintText = (selectedTestRule) => {
  if (!selectedTestRule?.test) {
    return "Select a test to upload files.";
  }
  return selectedTestRule.is_ml === 1
    ? "You can select multiple files at once."
    : "Only one file can be selected per upload for this test.";
};

// Checks if the selected test name and file names match for metals selection.
const hasMetalsSelectionMismatch = (selectedTestName, fileNames) => {
  const normalizedSelectedTestName = selectedTestName.trim().toLowerCase();
  const isMetalsSelection = ["metals", "metals agilent"].includes(normalizedSelectedTestName);
  if (!isMetalsSelection) {
    return false;
  }
  const hasAgilentInFileName = fileNames.some((fileName) => fileName.includes("agilent"));
  return (normalizedSelectedTestName === "metals agilent") !== hasAgilentInFileName;
};

// Returns an error message if any file fails validation, otherwise null.
const getFileTypeValidationError = ({ incomingFiles, fileTypeList, isMlParsingEnabled }) => {
  const hasInvalidFileType = incomingFiles.some(
    (file) => !fileMatchesUploadFileTypes(file, fileTypeList),
  );
  if (!hasInvalidFileType) {
    return null;
  }
  return isMlParsingEnabled
    ? "Please upload Excel or image files."
    : "Selected test only supports Excel files.";
};

// Returns the file extension and category class name for display badges based on the file name
const getFileDisplayMetadata = (fileName) => {
  const fullName = String(fileName || "");
  const parts = fullName.split(".");
  const extension = (parts.length > 1 ? parts[parts.length - 1] : "file").toLowerCase();
  const extensionLabel = extension.slice(0, 4).toUpperCase();

  if (["xls", "xlsx", "csv"].includes(extension)) {
    return { extensionLabel, categoryClassName: "ChemUpload__FileBadge--spreadsheet" };
  }
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(extension)) {
    return { extensionLabel, categoryClassName: "ChemUpload__FileBadge--image" };
  }
  return { extensionLabel, categoryClassName: "ChemUpload__FileBadge--default" };
};

const ChemUpload = () => {
  const [view, setView] = useState("Upload");
  const [testList, setTestList] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [files, setFiles] = useState([]);
  const [testModal, setTestModal] = useState(null);
  const [uploadResultsModal, setUploadResultsModal] = useState(null);
  const [loading, setLoading] = useState(false);

  const hasEditTestPermission = hasPermission("edit_chemistry_upload_tests");
  const hasDeleteTestPermission = hasPermission("delete_chemistry_upload_tests");
  const selectedTestDetails = buildSelectedTestDetails(
    selectedTest,
    selectedTest && selectedTest.is_ml === 1,
  );
  const resolveActiveUploadMethodKey = (routingFile) => (
    resolveChemUploadMethodKeyFromFileAndTest({
      file: routingFile,
      selectedTest,
    })
  );
  const shouldHideEditIcon = ["fap", "residualSolvents", "metals", "metalsAgilent"].includes(
    resolveActiveUploadMethodKey(files[0]?.file),
  );

  // Handles the test change event
  const handleTestChange = (nextSelectedTestName) => {
    if (!nextSelectedTestName) {
      setSelectedTest(null);
      setFiles([]);
      return;
    }
    const nextSelectedTest = testList.find((testItem) => testItem.test === nextSelectedTestName) || null;
    setSelectedTest(nextSelectedTest);
    setFiles([]);
  };

  const testSelectOptions = testList
    .filter((item) => item.test != null && item.test !== "")
    .map((item) => ({ value: item.test, label: item.test }));

  // Handles opening the test modal for create/edit modes.
  const handleOpenTestModal = (mode = "create") => {
    if (mode === "edit" && (!selectedTest || !selectedTest.test)) {
      return;
    }
    setTestModal({ mode });
  };

  // Handles the close test modal event
  const handleCloseTestModal = () => {
    setTestModal(null);
  };

  const resetUploadViewState = () => {
    setFiles([]);
    setSelectedTest(null);
    setUploadResultsModal(null);
    setTestModal(null);
  };

  const handleViewChange = (nextView) => {
    if (loading) {
      return;
    }
    if (nextView !== view) {
      resetUploadViewState();
    }
    setView(nextView);
  };

  // Fetches the test rules
  const fetchAndSetTests = async () => {
    try {
      const response = await getTestRules();
      if (response.success) {
        setTestList(response.result);
        return;
      }
      toast.error(response?.message || "Something went wrong.");
    } catch (error) {
      toast.error("Failed to fetch tests");
    }
  };

  // Fetches the test rules on component mount
  useEffect(() => {
    fetchAndSetTests();
    return () => {
      setTestList([]);
    };
  }, []);

  // Keeps selected test in sync with latest test list data after fetch/refresh.
  useEffect(() => {
    setSelectedTest((previousSelectedTest) => {
      const previousSelectedTestName = previousSelectedTest?.test;
      if (!previousSelectedTestName) {
        return previousSelectedTest;
      }
      const refreshedSelectedTest = testList.find(
        (testItem) => testItem.test === previousSelectedTestName,
      ) || null;
      return refreshedSelectedTest;
    });
  }, [testList]);

  // Removes the file at the given index from the selected files list
  const removeFileAt = (fileIndex) => setFiles((previousFiles) => (
    previousFiles.filter((_, index) => index !== fileIndex)
  ));

  // Creates the upload methods map for the selected test.
  const uploadMethods = useMemo(
    () => createChemUploadMethodMap({
      selectedTest,
      testList,
      setUploadResultsModal,
      setFiles,
    }),
    [
      selectedTest,
      testList,
      setUploadResultsModal,
      setFiles,
    ],
  );

  // Validates the upload files.
  const getUploadValidationError = ({ incomingFiles, requireFiles }) => {
    if (!selectedTest || !selectedTest.test) {
      return "Please select a test";
    }
    if (requireFiles && !incomingFiles.length) {
      return "Please select files to upload";
    }

    const fileNameFromEntry = (fileEntry) => {
      if (fileEntry?.name) {
        return fileEntry.name;
      }
      if (fileEntry?.file?.name) {
        return fileEntry.file.name;
      }
      return "";
    };

    const normalizedFiles = incomingFiles.map((fileEntry) => (
      fileEntry?.originFileObj || fileEntry?.file || fileEntry
    ));

    const uploadMethodKey = resolveActiveUploadMethodKey(normalizedFiles[0]);
    const selectedTestUploadEntry = uploadMethods[uploadMethodKey];
    if (!selectedTestUploadEntry?.uploadMethod) {
      return "Selected test is not supported.";
    }

    // Only ML tests accept multiple files in one action; all other upload methods are sequential single-file.
    if (incomingFiles.length > 1 && selectedTest.is_ml !== 1) {
      return "Only one file can be uploaded for this test.";
    }

    const selectedTestNameLower = selectedTest.test.trim().toLowerCase();
    const normalizedFileNames = normalizedFiles.map((fileEntry) => (
      fileNameFromEntry(fileEntry).trim().toLowerCase()
    ));
    const hasInvalidName = normalizedFileNames.some((fileName) => {
      if (fileName.includes(selectedTestNameLower)) {
        return false;
      }
      if (selectedTest.is_ml !== 1 && isLegacyChemUploadFileNamePairAllowed(selectedTest, fileName)) {
        return false;
      }
      return true;
    });
    const hasInvalidMetalsSelection = hasMetalsSelectionMismatch(
      selectedTest.test,
      normalizedFileNames,
    );
    if (hasInvalidName || hasInvalidMetalsSelection) {
      return "File name must include the selected test name";
    }

    return getFileTypeValidationError({
      incomingFiles: normalizedFiles,
      fileTypeList: selectedTestUploadEntry.acceptedFileTypes,
      isMlParsingEnabled: selectedTest.is_ml === 1,
    });
  };

  // Handles the confirm event for the upload results modal.
  // - Force-close: keep files and selected test so the user can retry.
  // - Clean close: workflow is done, so reset both.
  const handleUploadResultsModalConfirm = (outcome = {}) => {
    setUploadResultsModal(null);
    if (outcome.wasForceClose) {
      return;
    }
    setFiles([]);
    setSelectedTest(null);
  };

  // Handles the drop files event
  const handleDropFiles = (acceptedFiles) => {
    const fileSelectionError = getUploadValidationError({
      incomingFiles: acceptedFiles,
      requireFiles: false,
    });
    if (fileSelectionError) {
      toast.error(fileSelectionError);
      return;
    }
    setFiles(acceptedFiles.map((file) => ({ file })));
  };

  // Handles the upload change event
  const handleUploadChange = (info) => {
    const { fileList } = info;
    const selectedFiles = fileList
      .map((uploadedFile) => uploadedFile?.originFileObj || uploadedFile?.file || null)
      .filter(Boolean);

    handleDropFiles(selectedFiles);
  };

  /*
  * Handles the upload event
  * Uploads the files to the server and processes the results
  */
  const handleUpload = async () => {
    const uploadValidationError = getUploadValidationError({
      incomingFiles: files,
      requireFiles: true,
    });
    if (uploadValidationError) {
      toast.error(uploadValidationError);
      return;
    }
    setLoading(true);
    try {
      const uploadMethodKey = resolveActiveUploadMethodKey(files[0].file);
      const uploadHandler = uploadMethods[uploadMethodKey]?.uploadMethod;
      if (!uploadHandler) {
        toast.error("Selected test is not supported.");
        return;
      }
      await uploadHandler(files);
    } catch (error) {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ChemUpload" aria-busy={loading}>
      <Segmented
        block
        className="CustomSegmented"
        value={view}
        options={[{ value: "Upload", label: "Upload" }, { value: "History", label: "History" }]}
        onChange={handleViewChange}
        disabled={loading}
      />
      {view === "History" && <ChemHistory />}
      {view === "Upload" && (
        <>
          <div className="ChemUpload__Header">
            <div className="ChemUpload__HeaderLeft">
              <div className="ChemUpload__SelectorSection">
                <div className="ChemUpload__Label">
                  <span className="textRed">* </span>
                  Select a Test
                </div>
                <Select
                  allowClear
                  showSearch
                  className="ChemUpload__Select"
                  value={selectedTest?.test}
                  placeholder="Select a Test"
                  disabled={loading}
                  options={testSelectOptions}
                  filterOption={(inputValue, option) => (
                    option?.label?.toLowerCase().startsWith(inputValue?.toLowerCase())
                  )}
                  listHeight={400}
                  onChange={handleTestChange}
                />
                <div className="ChemUpload__SelectedTestDetailsSlot">
                  <div className="ChemUpload__SelectedTestDetailsRow">
                    {selectedTest && selectedTestDetails.length > 0 && (
                      <>
                        <div className="ChemUpload__SelectedTestDetails" role="status" aria-live="polite">
                          {selectedTestDetails.map((detailItem) => (
                            <span className="ChemUpload__SelectedTestDetailItem" key={detailItem.label}>
                              <span className="ChemUpload__SelectedTestDetailLabel">
                                {detailItem.label}
                                :
                              </span>
                              <span className="ChemUpload__SelectedTestDetailValue">{detailItem.value}</span>
                              <span className="ChemUpload__SelectedTestDetailSeparator">|</span>
                            </span>
                          ))}
                        </div>
                        {hasEditTestPermission && !shouldHideEditIcon && (
                          <div className="ChemUpload__SelectedTestDetailsEditWrap">
                            <Tooltip
                              title={
                                selectedTest.is_ml === 1
                                  ? ""
                                  : "The selected test is not editable."
                              }
                            >
                              <span>
                                <button
                                  type="button"
                                  className="ChemUpload__EditTestBtn"
                                  onClick={() => handleOpenTestModal("edit")}
                                  aria-label="Edit selected test"
                                  disabled={loading || selectedTest.is_ml !== 1}
                                >
                                  <EditOutlined />
                                </button>
                              </span>
                            </Tooltip>
                          </div>
                        )}
                      </>
                    )}
                    {!selectedTest && (
                      <div className="ChemUpload__SelectedTestDetailsPlaceholder">
                        Select a test to view details.
                      </div>
                    )}
                    {selectedTest && selectedTestDetails.length === 0 && (
                      <div className="ChemUpload__SelectedTestDetailsPlaceholder">
                        Upload excel file.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <Button
              className="chemAddTestBtn"
              onClick={() => handleOpenTestModal("create")}
              disabled={loading}
            >
              + Add Test
            </Button>
          </div>
          <div className="ChemUpload__UploadSection">
            {!files.length && (
              <div className="ChemUpload__UploadArea">
                <div className="ChemUpload__UploadHeader">Upload Files</div>
                <Upload.Dragger
                  key={selectedTest?.test}
                  multiple={selectedTest?.is_ml === 1}
                  accept={
                    uploadMethods[resolveActiveUploadMethodKey(null)]?.acceptedFileTypes?.join(",")
                    || ""
                  }
                  fileList={files.map((fileItem, fileIndex) => {
                    const sourceFile = fileItem?.file;
                    const fileName = sourceFile?.name || `file-${fileIndex + 1}`;
                    const lastModified = sourceFile?.lastModified || fileIndex;
                    return {
                      uid: `${fileName}-${lastModified}-${fileIndex}`,
                      name: fileName,
                      status: "done",
                      originFileObj: sourceFile,
                    };
                  })}
                  showUploadList={false}
                  beforeUpload={() => false}
                  disabled={loading}
                  customRequest={({ onSuccess }) => {
                    if (onSuccess) {
                      onSuccess("ok");
                    }
                  }}
                  onChange={handleUploadChange}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag file to this area to upload</p>
                  <p className="ant-upload-hint">
                    {getUploadSelectionHintText(selectedTest)}
                  </p>
                </Upload.Dragger>
                <div
                  className={`ChemUpload__SupportText ${
                    selectedTest
                    && !isUploadMethodSupported(uploadMethods[resolveActiveUploadMethodKey(null)])
                      ? "ChemUpload__SupportText--unsupported"
                      : ""
                  }`}
                >
                  {selectedTest
                    ? getUploadSupportText(
                      uploadMethods[resolveActiveUploadMethodKey(null)],
                      selectedTest.is_ml === 1,
                    )
                    : "Select a test to see supported file types."}
                </div>
              </div>
            )}
            {files.length > 0 && (
              <>
                <div className="ChemUpload__UploadHeader">Selected Files</div>
                <div className="ChemUpload__PreviewGrid">
                  {files.map((fileItem, fileIndex) => {
                    const fileMetadata = getFileDisplayMetadata(fileItem.file.name);
                    return (
                      <div
                        className="ChemUpload__PreviewGridItem"
                        key={`${fileItem.file.name}-${fileIndex}`}
                      >
                        <div className="ChemUpload__PreviewCard">
                          <div>
                            <div className={`ChemUpload__FileBadge ${fileMetadata.categoryClassName}`}>
                              {fileMetadata.extensionLabel}
                            </div>
                            <label className="ChemUpload__PreviewFileName">{fileItem.file.name}</label>
                          </div>
                          <div
                            className={`ChemUpload__RemoveFile ${loading ? "ChemUpload__RemoveFile--disabled" : ""}`}
                            onClick={loading ? undefined : () => removeFileAt(fileIndex)}
                            role="button"
                            tabIndex={loading ? -1 : 0}
                            aria-disabled={loading}
                          >
                            <DeleteOutlined />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  className={`ChemUpload__SupportText ${
                    selectedTest
                    && !isUploadMethodSupported(uploadMethods[resolveActiveUploadMethodKey(files[0].file)])
                      ? "ChemUpload__SupportText--unsupported"
                      : ""
                  }`}
                >
                  {selectedTest
                    ? getUploadSupportText(
                      uploadMethods[resolveActiveUploadMethodKey(files[0].file)],
                      selectedTest.is_ml === 1,
                    )
                    : "Select a test to see supported file types."}
                </div>
              </>
            )}
          </div>
          <div className="ChemUpload__uploadBtnContainer">
            <Button
              type="primary"
              onClick={handleUpload}
              loading={loading}
              className="ChemUpload__uploadBtn"
              disabled={
                !files.length
                || !selectedTest?.test
                || !uploadMethods[resolveActiveUploadMethodKey(files[0].file)]?.uploadMethod
              }
            >
              Upload
            </Button>
          </div>
        </>
      )}
      {loading && (
        <div className="ChemUpload__InteractionOverlay">
          <div className="ChemUpload__InteractionOverlayContent">
            <Spin size="large" />
            <div className="ChemUpload__InteractionOverlayText">
              Uploading files and processing results.
              <br />
              Please wait.
              <br />
              This can take a while.
            </div>
          </div>
        </div>
      )}
      <ChemAddNewTest
        isOpen={testModal != null}
        handleHidePopup={handleCloseTestModal}
        getTestRules={fetchAndSetTests}
        mode={testModal?.mode ?? "create"}
        initialTestData={testModal && testModal.mode === "edit" ? selectedTest : null}
        canDeleteTest={hasDeleteTestPermission}
      />
      <ChemUploadResultsModal
        isOpen={uploadResultsModal != null}
        testName={uploadResultsModal?.testName}
        resultRows={uploadResultsModal?.resultRows}
        isMlUpload={uploadResultsModal?.isMlUpload}
        onConfirm={handleUploadResultsModalConfirm}
      />
    </div>
  );
};

export default ChemUpload;
